import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  Status,
  ThreeDotMenu,
  ThreeDotMenuItem,
  Tooltip,
} from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { FormattedConnectionModel } from '@mm/state/model/ConnectionModel';
import {
  GOOGLE_SHEET,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import {
  activeIntegrationSelector,
  orderedConnectionsSelector,
  selectGoogleSheetsConnectionsByAccountId,
} from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectGoogleAccounts } from '@mm/state/selectors/googleSheetSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { colors } from '@mm/utils';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IntegrationErrors } from '../../../constants';
import { useFetchConnections } from '../../../use-fetch-connections';
import { GoogleSheets } from './GoogleSheets';
import './GoogleSheetsIntegration.scss';

type CellProps = { item: FormattedConnectionModel };

const renderErrorIcon = item => {
  const haveError =
    item.webhookErrors?.length &&
    item.webhookErrors
      .map(err => err.errorDetails)
      .some(fullError =>
        IntegrationErrors.some(errorSubstring =>
          fullError.toLowerCase().includes(errorSubstring),
        ),
      );

  if (haveError) {
    return (
      <Tooltip
        title="   Please check your Google Account. Make sure that access is granted(not
        expired) and grid limits are not exceeded."
      >
        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
      </Tooltip>
    );
  }
};

const renderNameCell = (
  item: FormattedConnectionModel,
  editSheet: (id: number) => void,
) => (
  <div
    className="connection-table__clickable-cell"
    onClick={() => editSheet(item.id)}
  >
    {item.name} {renderErrorIcon(item)}
  </div>
);

const renderStatusCell = ({ item }: CellProps) => {
  const { statusText, active } = item;
  return (
    <Status
      status={statusText}
      color={!active ? colors.grayLight : undefined}
    />
  );
};

const renderActionsCell = (
  item: FormattedConnectionModel,
  editSheet: (id: number) => void,
  deleteGoogleSheet: (item: FormattedConnectionModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return deleteGoogleSheet(item);
      default:
        break;
    }
  };

  const elements: ThreeDotMenuItem[] = [
    {
      name: 'Delete',
      value: 'delete',
      children: (
        <div className="connection-table__action-cell-item">
          <div className="connection-table__action-cell-item_icon">
            <DeleteOutlined />
          </div>
          <div>Delete</div>
        </div>
      ),
      hideName: true,
    },
  ];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="connection-table__action-cell">
      <EditOutlined
        onClick={() => editSheet(item.id)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </div>
  );
};

const generateTableColumns = (
  editSheet: (id: number) => void,
  deleteGoogleSheet: (item: FormattedConnectionModel) => void,
) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.name.localeCompare(b.name),
      render: (text, record: FormattedConnectionModel) =>
        renderNameCell(record, editSheet),
      width: '15%',
    },
    {
      title: 'Spreadsheet name',
      dataIndex: 'googleSpreadsheetTitle',
      key: 'googleSpreadsheetTitle',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        (a.googleSpreadsheetTitle || '').localeCompare(
          b.googleSpreadsheetTitle || '',
        ),
      render: (text, record) => record.googleSpreadsheetTitle,
      width: '30%',
    },
    {
      title: 'Worksheet name',
      dataIndex: 'googleSheetTitle',
      key: 'googleSheetTitle',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        (a.googleSheetTitle || '').localeCompare(b.googleSheetTitle || ''),
      render: (text, record) => record.googleSheetTitle,
      width: '30%',
    },
    {
      title: 'Status',
      dataIndex: 'statusText',
      key: 'statusText',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.statusText.localeCompare(b.statusText),
      render: (text, record: FormattedConnectionModel) =>
        renderStatusCell({ item: record }),
      width: '10%',
    },
    {
      title: 'Created',
      dataIndex: 'formattedCreatedAt',
      key: 'formattedCreatedAt',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        new Date(a.formattedCreatedAt).getTime() -
        new Date(b.formattedCreatedAt).getTime(),
      render: (text, record) => record.formattedCreatedAt,
      width: '10%',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, record: FormattedConnectionModel) =>
        renderActionsCell(record, editSheet, deleteGoogleSheet),

      width: '5%',
    },
  ];
  return columns;
};

export const GoogleSheetsIntegration = () => {
  const { setActiveIntegration } = useConnectionsTableActions();
  const accounts = useSelector(selectGoogleAccounts);
  const activeIntegration = useSelector(activeIntegrationSelector);
  const { getAccounts, disconnectAccount } = useGoogleSheetsCRUDActions();
  const { fetch } = useFetchConnections();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const connections = useSelector(orderedConnectionsSelector);
  const googleSheetsConnections = useSelector(
    selectGoogleSheetsConnectionsByAccountId,
  );
  const { deleteConnection } = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal, openGeneralConfirmModal } = useModalActions();

  useEffect(() => {
    getAccounts(facebookPageId);
    fetch(1, 200, 'google_spreadsheets');
  }, []);

  const onDisconnect = (id: number) => {
    const accountEmail = accounts?.find(acc => acc.id === id)?.email;
    openGeneralConfirmModal({
      headerText: `Are you sure you want to disconnect ${accountEmail} account from Google Sheets Integration?`,
      messageText: '',
      onCloseHandler: ok => {
        if (ok) {
          disconnectAccount(id, facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      okButtonStyling: { red: true },
      cancelButtonText: 'Cancel',
      cancelButtonFirst: true,
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };

  const editGoogleSheet = (id: number) => {
    setActiveIntegrationHandler();
    showSlideoutMenu('googleSheetIntegrationEditor', {
      widgetId: id,
    });
  };
  const deleteGoogleSheet = (item: FormattedConnectionModel) => {
    const deleteCallback = async () => {
      deleteConnection(facebookPageId, item);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${item.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };
  const setActiveIntegrationHandler = () => {
    if (activeIntegration === GOOGLE_SHEET) return;
    setActiveIntegration(GOOGLE_SHEET);
  };

  const columns = generateTableColumns(editGoogleSheet, deleteGoogleSheet);

  return (
    <GoogleSheets
      isAccountConnected={!!accounts.length}
      spreadsheetCount={
        connections.filter(c => c.integrationType === 'google_spreadsheet')
          .length
      }
      onDisconnect={onDisconnect}
      columns={columns}
      googleSheetsConnections={googleSheetsConnections}
      name={GOOGLE_SHEET}
    />
  );
};
